import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import {
  UploadCloud,
  FileSpreadsheet,
  FileArchive,
  Download,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  X,
  RefreshCw,
  Search,
  Music,
  Disc3,
  Users,
  Check,
  Calendar,
  Layers,
  ArrowRight,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { Spinner } from "../ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

export default function BulkImport() {
  const [file, setFile] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState("default");
  const [selectedStatus, setSelectedStatus] = useState("Released");

  // Filtering & Pagination state
  const [filterTab, setFilterTab] = useState("all"); // 'all' | 'valid' | 'errors'
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importSuccessData, setImportSuccessData] = useState(null);

  const fileInputRef = useRef(null);

  // Fetch users for target assignment
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get("/admin/configure/users");
      if (res.data?.ok && res.data?.data) {
        setUsersList(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch users list:", err);
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    await processFile(selectedFile);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      await processFile(droppedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const processFile = async (selectedFile) => {
    const ext = selectedFile.name.split(".").pop().toLowerCase();
    if (!["csv", "xlsx", "xls", "zip"].includes(ext)) {
      toast.error("Invalid file format. Please upload a .csv, .xlsx, .xls, or .zip file.");
      return;
    }

    setFile(selectedFile);
    setImportSuccessData(null);
    setValidationResult(null);
    setIsValidating(true);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await axios.post("/admin/configure/bulk-import/validate", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.ok && res.data?.data) {
        setValidationResult(res.data.data);
        if (res.data.data.summary.hasErrors) {
          toast.warning(
            `Found ${res.data.data.summary.invalidRows} records with validation errors. Review them below.`
          );
        } else {
          toast.success("File validated successfully! All records are valid.");
        }
      }
    } catch (err) {
      console.error("Validation failed:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to validate file";
      toast.error(errMsg);
      setValidationResult(null);
    } finally {
      setIsValidating(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadTemplate = async (format = "csv") => {
    try {
      const res = await axios.get(`/admin/configure/bulk-import/template?format=${format}`, {
        responseType: "blob",
      });

      const blobType =
        format === "xlsx"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "text/csv;charset=utf-8;";

      const blob = new Blob([res.data], { type: blobType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute(
        "download",
        `RayApp_Bulk_Import_Template.${format === "xlsx" ? "xlsx" : "csv"}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`Sample ${format.toUpperCase()} template downloaded`);
    } catch (err) {
      console.warn("Backend template download failed, generating client-side:", err);
      // Fallback: Generate directly on client using XLSX
      try {
        const sampleRows = [
          {
            "Album Title": "Echoes of the Past",
            "Album Primary Artist": "Aarav Sharma",
            "Album Secondary Artist": "Meera Das",
            "Language": "Hindi",
            "Main Genre": "Pop",
            "Sub Genre": "Contemporary Pop",
            "Release Date": "2018-06-15",
            "Production Year": 2018,
            "Record Label": "Swaram Records",
            "UPC": "8901234567890",
            "Status": "Released",
            "User Email": "artist@example.com",
            "Track Number": 1,
            "Track Title": "Rooh Ki Awaaz",
            "Track Primary Artist": "Aarav Sharma",
            "Track Secondary Artist": "",
            "Composer": "Aarav Sharma",
            "Lyricist": "Rohan Verma",
            "Track Language": "Hindi",
            "Track Main Genre": "Pop",
            "Track Sub Genre": "Contemporary Pop",
            "Explicit": "No",
            "ISRC": "INA011800001",
            "Audio Filename": "rooh_ki_awaaz.mp3",
          },
          {
            "Album Title": "Echoes of the Past",
            "Album Primary Artist": "Aarav Sharma",
            "Album Secondary Artist": "",
            "Language": "Hindi",
            "Main Genre": "Pop",
            "Sub Genre": "Love Songs",
            "Release Date": "2018-06-15",
            "Production Year": 2018,
            "Record Label": "Swaram Records",
            "UPC": "8901234567890",
            "Status": "Released",
            "User Email": "artist@example.com",
            "Track Number": 2,
            "Track Title": "Yaadon Ka Safar",
            "Track Primary Artist": "Aarav Sharma",
            "Track Secondary Artist": "Meera Das",
            "Composer": "Aarav Sharma",
            "Lyricist": "Rohan Verma",
            "Track Language": "Hindi",
            "Track Main Genre": "Pop",
            "Track Sub Genre": "Love Songs",
            "Explicit": "No",
            "ISRC": "INA011800002",
            "Audio Filename": "yaadon_ka_safar.mp3",
          },
          {
            "Album Title": "Midnight Monsoon",
            "Album Primary Artist": "The Chennai Trio",
            "Album Secondary Artist": "",
            "Language": "Tamil",
            "Main Genre": "Indie",
            "Sub Genre": "Indie Folk",
            "Release Date": "2021-11-20",
            "Production Year": 2021,
            "Record Label": "Independent Vibes",
            "UPC": "8909876543210",
            "Status": "Released",
            "User Email": "",
            "Track Number": 1,
            "Track Title": "Mazhai Kaalam",
            "Track Primary Artist": "The Chennai Trio",
            "Track Secondary Artist": "",
            "Composer": "Karthik R",
            "Lyricist": "Vairamuthu K",
            "Track Language": "Tamil",
            "Track Main Genre": "Indie",
            "Track Sub Genre": "Indie Folk",
            "Explicit": "No",
            "ISRC": "INA012100003",
            "Audio Filename": "",
          },
        ];

        const ws = XLSX.utils.json_to_sheet(sampleRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Metadata_Template");

        if (format === "xlsx") {
          XLSX.writeFile(wb, "RayApp_Bulk_Import_Template.xlsx");
        } else {
          const csv = XLSX.utils.sheet_to_csv(ws);
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.setAttribute("download", "RayApp_Bulk_Import_Template.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(link.href);
        }
        toast.success(`Sample ${format.toUpperCase()} template downloaded`);
      } catch (clientErr) {
        console.error("Client template generation error:", clientErr);
        toast.error("Failed to download template");
      }
    }
  };

  const resetImport = () => {
    setFile(null);
    setValidationResult(null);
    setImportSuccessData(null);
    setSearchQuery("");
    setFilterTab("all");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleExecuteImport = async () => {
    if (!file || !validationResult) return;
    if (validationResult.summary?.hasErrors) {
      toast.error("Cannot import file with errors. All records must pass validation.");
      return;
    }

    setConfirmDialogOpen(false);
    setIsImporting(true);

    const formData = new FormData();
    formData.append("file", file);
    if (selectedOwnerId && selectedOwnerId !== "default") {
      formData.append("defaultOwnerId", selectedOwnerId);
    }
    formData.append("defaultStatus", selectedStatus);

    try {
      const res = await axios.post("/admin/configure/bulk-import/execute", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.ok && res.data?.data) {
        setImportSuccessData(res.data.data);
        toast.success(res.data.data.message || "Import completed successfully!");
      }
    } catch (err) {
      console.error("Import failed:", err);
      const errMsg =
        err.response?.data?.message || err.message || "Failed to execute bulk import";
      toast.error(errMsg);
    } finally {
      setIsImporting(false);
    }
  };

  // Filter records for table view
  const filteredRecords = React.useMemo(() => {
    if (!validationResult?.records) return [];

    return validationResult.records.filter((rec) => {
      // Tab filter
      if (filterTab === "valid" && !rec.isValid) return false;
      if (filterTab === "errors" && rec.isValid) return false;

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = rec.albumTitle?.toLowerCase().includes(q);
        const matchTrack = rec.trackTitle?.toLowerCase().includes(q);
        const matchArtist = rec.albumPrimaryArtist?.toLowerCase().includes(q);
        const matchIsrc = rec.isrc?.toLowerCase().includes(q);
        const matchUpc = rec.upc?.toLowerCase().includes(q);
        const matchComposer = rec.composer?.toLowerCase().includes(q);
        return matchTitle || matchTrack || matchArtist || matchIsrc || matchUpc || matchComposer;
      }

      return true;
    });
  }, [validationResult, filterTab, searchQuery]);

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6 max-w-7xl mx-auto w-full pb-16">
        {/* Header & Template Downloads */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-2 border-b">
          <div>
            <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              Bulk Metadata & Album Import
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Import catalog metadata and historical releases in bulk. Audio is optional; upload a CSV/Excel or a ZIP with audio files.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("csv")}
              className="h-9 gap-1.5 border-dashed"
            >
              <Download className="h-4 w-4 text-muted-foreground" />
              Sample CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate("xlsx")}
              className="h-9 gap-1.5 border-dashed"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              Sample Excel (.xlsx)
            </Button>
          </div>
        </div>

        {/* Global Import Settings / Defaults */}
        <Card className="bg-muted/20 border-dashed">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Default Target Owner / Artist
              </label>
              <Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
                <SelectTrigger className="h-9 bg-background">
                  <SelectValue placeholder="Current Admin / Logged-in User" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="default">
                    Current Admin (or user in CSV row)
                  </SelectItem>
                  {usersList.map((user) => (
                    <SelectItem key={user._id} value={user._id}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <Disc3 className="h-3.5 w-3.5" />
                Default Release Status
              </label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="h-9 bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Released">Released (Live Catalog)</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 flex flex-col justify-center">
              <span className="text-xs font-medium text-muted-foreground">
                Historical Releases Note:
              </span>
              <p className="text-xs text-muted-foreground/80 leading-snug">
                Past production years (e.g. 1995, 2010) are fully supported for back-catalog ingestion.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Upload Zone */}
        {!file && !importSuccessData && (
          <Card className="overflow-hidden border-2 border-dashed transition-all hover:border-primary/50">
            <CardContent className="p-8">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv,.xlsx,.xls,.zip"
                className="hidden"
                id="bulk-import-file-input"
              />
              <label
                htmlFor="bulk-import-file-input"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="flex flex-col items-center justify-center cursor-pointer text-center py-8 group"
              >
                <div className="p-4 rounded-full bg-muted/60 group-hover:bg-primary/10 group-hover:scale-110 transition-all duration-300 mb-4 ring-1 ring-border">
                  <UploadCloud className="h-10 w-10 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-foreground">
                  Click to browse or drag & drop file here
                </h3>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                  Upload metadata spreadsheet (<strong>.csv</strong>, <strong>.xlsx</strong>) or a <strong>.zip</strong> archive with metadata and optional audio files (.mp3, .wav, .flac).
                </p>

                <div className="flex items-center gap-3 mt-5">
                  <Badge variant="outline" className="text-xs py-1 px-3 gap-1 font-normal bg-background">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" /> CSV / XLSX
                  </Badge>
                  <Badge variant="outline" className="text-xs py-1 px-3 gap-1 font-normal bg-background">
                    <FileArchive className="h-3.5 w-3.5 text-amber-600" /> ZIP + Optional Audio
                  </Badge>
                </div>
              </label>
            </CardContent>
          </Card>
        )}

        {/* Validating Spinner state */}
        {isValidating && (
          <Card className="p-12 flex flex-col items-center justify-center gap-4 text-center">
            <Spinner className="h-8 w-8 text-primary animate-spin" />
            <div>
              <h3 className="font-semibold text-base">Validating Metadata File...</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Parsing records, checking field limits, genres, and all required metadata rules.
              </p>
            </div>
          </Card>
        )}

        {/* File Active & Summary Card */}
        {file && !isValidating && validationResult && !importSuccessData && (
          <div className="space-y-6">
            {/* Active file banner */}
            <div className="flex items-center justify-between p-4 bg-muted/40 rounded-xl border">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-background border">
                  {file.name.endsWith(".zip") ? (
                    <FileArchive className="h-6 w-6 text-amber-600" />
                  ) : (
                    <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                  )}
                </div>
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    {file.name}
                    <Badge variant="secondary" className="text-[11px] font-normal">
                      {(file.size / 1024).toFixed(1)} KB
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {validationResult.summary.totalRows} records found across{" "}
                    {validationResult.summary.totalAlbums} album(s)
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetImport}
                  className="h-8 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Change File
                </Button>
              </div>
            </div>

            {/* Metric KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Card className="p-4 flex flex-col justify-between">
                <span className="text-xs text-muted-foreground font-medium">Total Rows</span>
                <div className="text-2xl font-bold mt-2">{validationResult.summary.totalRows}</div>
              </Card>

              <Card className="p-4 flex flex-col justify-between">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <Disc3 className="h-3.5 w-3.5 text-primary" />
                  Unique Albums
                </span>
                <div className="text-2xl font-bold mt-2">{validationResult.summary.totalAlbums}</div>
              </Card>

              <Card className="p-4 flex flex-col justify-between">
                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  <Music className="h-3.5 w-3.5 text-primary" />
                  Total Tracks
                </span>
                <div className="text-2xl font-bold mt-2">{validationResult.summary.totalTracks}</div>
              </Card>

              <Card className="p-4 flex flex-col justify-between bg-emerald-500/5 border-emerald-500/20">
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Valid Records
                </span>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">
                  {validationResult.summary.validRows}
                </div>
              </Card>

              <Card
                className={`p-4 flex flex-col justify-between ${
                  validationResult.summary.invalidRows > 0
                    ? "bg-destructive/10 border-destructive/30"
                    : "bg-muted/10"
                }`}
              >
                <span
                  className={`text-xs font-medium flex items-center gap-1 ${
                    validationResult.summary.invalidRows > 0
                      ? "text-destructive font-semibold"
                      : "text-muted-foreground"
                  }`}
                >
                  <AlertCircle className="h-3.5 w-3.5" />
                  Invalid Records
                </span>
                <div
                  className={`text-2xl font-bold mt-2 ${
                    validationResult.summary.invalidRows > 0
                      ? "text-destructive"
                      : "text-muted-foreground"
                  }`}
                >
                  {validationResult.summary.invalidRows}
                </div>
              </Card>
            </div>

            {/* Table Controls (Search & Filter Tabs) */}
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-2">
              <Tabs value={filterTab} onValueChange={setFilterTab} className="w-auto">
                <TabsList className="h-9">
                  <TabsTrigger value="all" className="text-xs">
                    All ({validationResult.records.length})
                  </TabsTrigger>
                  <TabsTrigger value="valid" className="text-xs">
                    Valid ({validationResult.summary.validRows})
                  </TabsTrigger>
                  <TabsTrigger
                    value="errors"
                    className={`text-xs ${
                      validationResult.summary.invalidRows > 0
                        ? "text-destructive font-medium data-[state=active]:text-destructive"
                        : ""
                    }`}
                  >
                    Errors ({validationResult.summary.invalidRows})
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="relative w-full sm:w-72">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search album, track, artist, ISRC..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-xs bg-background"
                />
              </div>
            </div>

            {/* Interactive Data Table with Error Highlighting */}
            <Card className="overflow-hidden border">
              <div className="overflow-x-auto max-h-[480px]">
                <Table className="relative">
                  <TableHeader className="bg-muted/60 sticky top-0 z-10 backdrop-blur">
                    <TableRow>
                      <TableHead className="w-14 text-center">Row</TableHead>
                      <TableHead className="w-24">Status</TableHead>
                      <TableHead className="min-w-[140px]">Album Title</TableHead>
                      <TableHead className="min-w-[140px]">Track Title</TableHead>
                      <TableHead className="min-w-[130px]">Primary Artist</TableHead>
                      <TableHead className="min-w-[90px]">Year</TableHead>
                      <TableHead className="min-w-[110px]">Release Date</TableHead>
                      <TableHead className="min-w-[120px]">Genre / Sub</TableHead>
                      <TableHead className="min-w-[120px]">Composer / Lyricist</TableHead>
                      <TableHead className="min-w-[100px]">ISRC</TableHead>
                      <TableHead className="min-w-[100px]">UPC</TableHead>
                      <TableHead className="min-w-[110px]">Audio File</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-10 text-muted-foreground">
                          No matching records found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRecords.map((rec) => {
                        const hasErrors = !rec.isValid;
                        const errorFieldMap = new Map(rec.errors.map((e) => [e.field, e.message]));

                        return (
                          <TableRow
                            key={rec.rowIndex}
                            className={`transition-colors ${
                              hasErrors
                                ? "bg-destructive/10 hover:bg-destructive/15 border-l-4 border-l-destructive text-foreground"
                                : "hover:bg-muted/50"
                            }`}
                          >
                            <TableCell className="text-center font-mono text-xs font-semibold text-muted-foreground">
                              {rec.rowIndex}
                            </TableCell>

                            <TableCell>
                              {rec.isValid ? (
                                <Badge
                                  variant="outline"
                                  className="text-[11px] font-normal bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 gap-1"
                                >
                                  <Check className="h-3 w-3" /> Valid
                                </Badge>
                              ) : (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge
                                      variant="destructive"
                                      className="text-[11px] font-medium gap-1 cursor-pointer"
                                    >
                                      <AlertTriangle className="h-3 w-3" />
                                      {rec.errors.length} Error{rec.errors.length > 1 ? "s" : ""}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-xs p-2 text-xs space-y-1">
                                    {rec.errors.map((e, idx) => (
                                      <div key={idx} className="text-destructive-foreground">
                                        • <strong>{e.field}</strong>: {e.message}
                                      </div>
                                    ))}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </TableCell>

                            {/* Album Title */}
                            <TableCell
                              className={`font-medium text-xs ${
                                errorFieldMap.has("albumTitle") ? "text-destructive font-bold underline" : ""
                              }`}
                            >
                              {rec.albumTitle || <span className="text-destructive italic">Missing</span>}
                            </TableCell>

                            {/* Track Title */}
                            <TableCell
                              className={`text-xs ${
                                errorFieldMap.has("trackTitle") ? "text-destructive font-bold underline" : ""
                              }`}
                            >
                              {rec.trackTitle || <span className="text-destructive italic">Missing</span>}
                            </TableCell>

                            {/* Primary Artist */}
                            <TableCell
                              className={`text-xs ${
                                errorFieldMap.has("albumPrimaryArtist") || errorFieldMap.has("trackPrimaryArtist")
                                  ? "text-destructive font-bold underline"
                                  : ""
                              }`}
                            >
                              {rec.trackPrimaryArtist || rec.albumPrimaryArtist || (
                                <span className="text-destructive italic">Missing</span>
                              )}
                            </TableCell>

                            {/* Production Year */}
                            <TableCell
                              className={`text-xs font-mono ${
                                errorFieldMap.has("productionYear") ? "text-destructive font-bold" : ""
                              }`}
                            >
                              {rec.productionYear || "-"}
                            </TableCell>

                            {/* Release Date */}
                            <TableCell
                              className={`text-xs font-mono ${
                                errorFieldMap.has("releaseDate") ? "text-destructive font-bold" : ""
                              }`}
                            >
                              {rec.releaseDate || "-"}
                            </TableCell>

                            {/* Genre / Subgenre */}
                            <TableCell className="text-xs">
                              <span className={errorFieldMap.has("mainGenre") ? "text-destructive font-bold" : ""}>
                                {rec.mainGenre}
                              </span>
                              {rec.subGenre && (
                                <span className="text-muted-foreground text-[11px] block">
                                  {rec.subGenre}
                                </span>
                              )}
                            </TableCell>

                            {/* Composer / Lyricist */}
                            <TableCell className="text-xs">
                              <div
                                className={errorFieldMap.has("composer") ? "text-destructive font-bold" : ""}
                              >
                                C: {rec.composer || "-"}
                              </div>
                              <div
                                className={`text-[11px] text-muted-foreground ${
                                  errorFieldMap.has("lyricist") ? "text-destructive font-bold" : ""
                                }`}
                              >
                                L: {rec.lyricist || "-"}
                              </div>
                            </TableCell>

                            {/* ISRC */}
                            <TableCell
                              className={`text-xs font-mono ${
                                errorFieldMap.has("isrc") ? "text-destructive font-bold" : "text-muted-foreground"
                              }`}
                            >
                              {rec.isrc || "-"}
                            </TableCell>

                            {/* UPC */}
                            <TableCell
                              className={`text-xs font-mono ${
                                errorFieldMap.has("upc") ? "text-destructive font-bold" : "text-muted-foreground"
                              }`}
                            >
                              {rec.upc || "-"}
                            </TableCell>

                            {/* Audio file */}
                            <TableCell className="text-xs">
                              {rec.audioFile ? (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${
                                    rec.audioFileFound === false
                                      ? "text-destructive border-destructive/40 bg-destructive/10"
                                      : "text-muted-foreground"
                                  }`}
                                >
                                  {rec.audioFile}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground/60 text-[11px]">Optional</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>

            {/* Validation Error List Summary below the Table */}
            {validationResult.summary.hasErrors && (
              <Card className="border-destructive/40 bg-destructive/5 overflow-hidden">
                <CardHeader className="pb-3 border-b border-destructive/20 bg-destructive/10">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <CardTitle className="text-sm font-semibold text-destructive">
                      Validation Errors ({validationResult.errorList.length} issues in{" "}
                      {validationResult.summary.invalidRows} rows)
                    </CardTitle>
                  </div>
                  <CardDescription className="text-xs text-destructive/80 mt-1">
                    <strong>All-or-None Policy:</strong> Either all records must pass validation and be imported, or 0 records will be imported. Please resolve the errors below in your file and re-upload.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 max-h-60 overflow-y-auto space-y-2">
                  {validationResult.errorList.map((err, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-2.5 rounded-lg bg-background/80 border border-destructive/20 text-xs"
                    >
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5">
                        Row {err.row}
                      </Badge>
                      <div className="flex-1">
                        <span className="font-semibold text-foreground">
                          {err.field}
                        </span>
                        <span className="text-muted-foreground">
                          {" "}
                          ({err.albumTitle ? `Album: "${err.albumTitle}"` : ""}{" "}
                          {err.trackTitle ? `Track: "${err.trackTitle}"` : ""})
                        </span>
                        <p className="text-destructive font-medium mt-0.5">{err.message}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Execution / Action Bar */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border">
              <div className="text-xs text-muted-foreground">
                {validationResult.summary.hasErrors ? (
                  <span className="text-destructive font-medium flex items-center gap-1.5">
                    <AlertCircle className="h-4 w-4" />
                    Fix {validationResult.summary.invalidRows} invalid record(s) to enable import.
                  </span>
                ) : (
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" />
                    Ready to import {validationResult.summary.totalAlbums} album(s) and{" "}
                    {validationResult.summary.totalTracks} track(s).
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" onClick={resetImport}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={validationResult.summary.hasErrors || isImporting}
                  onClick={() => setConfirmDialogOpen(true)}
                  className="gap-2 px-5"
                >
                  {isImporting ? (
                    <>
                      <Spinner className="h-4 w-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <UploadCloud className="h-4 w-4" />
                      Import All ({validationResult.summary.totalAlbums} Albums)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Post-Import Success State */}
        {importSuccessData && (
          <Card className="p-8 text-center border-emerald-500/30 bg-emerald-500/5 max-w-xl mx-auto space-y-6">
            <div className="flex justify-center">
              <div className="p-4 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-4 ring-emerald-500/20">
                <CheckCircle2 className="h-10 w-10" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-bold tracking-tight text-foreground">
                Bulk Import Successful!
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {importSuccessData.message}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto py-2">
              <div className="p-3 rounded-lg bg-background border">
                <span className="text-xs text-muted-foreground">Imported Albums</span>
                <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                  {importSuccessData.summary?.albumsCount || 0}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-background border">
                <span className="text-xs text-muted-foreground">Imported Tracks</span>
                <div className="text-2xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                  {importSuccessData.summary?.tracksCount || 0}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3 pt-2">
              <Button variant="outline" onClick={resetImport}>
                Import Another File
              </Button>
              <Button asChild className="gap-1.5">
                <a href="/all-releases">
                  View All Releases
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </Card>
        )}

        {/* Confirmation Modal */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Bulk Import</DialogTitle>
              <DialogDescription>
                You are about to import{" "}
                <strong>{validationResult?.summary?.totalAlbums} albums</strong> containing{" "}
                <strong>{validationResult?.summary?.totalTracks} tracks</strong> into the system.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-3 text-xs bg-muted/40 p-4 rounded-lg border">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Target Owner / User:</span>
                <span className="font-semibold text-foreground">
                  {selectedOwnerId === "default"
                    ? "Current Admin / Specified in CSV"
                    : usersList.find((u) => u._id === selectedOwnerId)?.name || selectedOwnerId}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Release Status:</span>
                <span className="font-semibold text-foreground">{selectedStatus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transactional Guarantee:</span>
                <span className="text-emerald-600 font-medium">All-or-None Atomic Commit</span>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleExecuteImport} className="gap-2">
                <UploadCloud className="h-4 w-4" />
                Confirm & Import
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
